import { Stack } from '@mui/material';
import { PayableFeeStatus } from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { PAYABLE_FEE_STATUSES } from './FeeStatusSelectMultiple';
import { FeeStatusTagSelect, FeeStatusTagSelectProps } from './FeeStatusTagSelect';

type FeeStatusExpandedSelectProps = PropsWithChildren<{
  selectedStatuses: PayableFeeStatus[];
  onApply: (v: PayableFeeStatus[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;
export const FeeStatusExpandedSelect: FC<FeeStatusExpandedSelectProps> = ({
  selectedStatuses: initialSelectedStatuses,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedStatuses, setSelectedStatuses] =
    useState<PayableFeeStatus[]>(initialSelectedStatuses);

  const toggleStatuses = useCallback((v: PayableFeeStatus[]) => {
    setSelectedStatuses((statuses) => toggleMultipleValueArrayProperty(statuses, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedStatuses);
    onClose();
  }, [selectedStatuses, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderFeeStatusTags({
            statuses: PAYABLE_FEE_STATUSES,
            tagProps: (status) => ({
              variant: selectedStatuses.includes(status) ? 'filled' : undefined,
            }),
            onClick: (status) => toggleStatuses([status]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleStatuses, selectedStatuses]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedStatuses.length > 0}
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
    >
      {renderFeeStatusTags({
        statuses: selectedStatuses,
        tagProps: { sx: { maxWidth: 200 }, size: 'small' },
        onDelete: (status) => toggleStatuses([status]),
      })}
    </ExpandedSelect>
  );
};

type RenderFeeStatusTagsParams = {
  statuses: PayableFeeStatus[];
  onDelete?: (v: PayableFeeStatus) => void;
  onClick?: (v: PayableFeeStatus) => void;
  tagProps?:
    | Omit<FeeStatusTagSelectProps, 'status'>
    | ((v: PayableFeeStatus) => Omit<FeeStatusTagSelectProps, 'status'>);
};

export const renderFeeStatusTags = ({
  statuses,
  onDelete,
  onClick,
  tagProps,
}: RenderFeeStatusTagsParams) =>
  statuses.map((v) => (
    <FeeStatusTagSelect
      key={v}
      status={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
